import React, { useEffect, useState, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import { getGoogleMapsApiKey } from '../config/maps'
import { getUsers, getUserPermissions, updateUserPermissions } from '../services/users'
import { getInspections } from '../services/inspections'
import { getTreatments } from '../services/treatments'
import { getHarvests } from '../services/harvests'
import { getMovements } from '../services/movements'
import { getYards, getInventory } from '../services/api'
import { getUserLocations, UserLocation } from '../services/locations'
import { User, PermissionInfo } from '../types'

/**
 * Route polyline component
 */
function RoutePolyline({ locations, extraPoints = [] }: { locations: UserLocation[]; extraPoints?: { lat: number; lng: number }[] }) {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const path = locations.map(loc => ({ lat: loc.latitude, lng: loc.longitude }))

    const polyline = path.length > 0 ? new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#2196F3',
      strokeOpacity: 0.85,
      strokeWeight: 4,
    }) : null
    if (polyline) polyline.setMap(map)

    // Fit map to the route AND all site/warehouse markers so everything is visible
    const bounds = new google.maps.LatLngBounds()
    let any = false
    path.forEach(p => { bounds.extend(p); any = true })
    extraPoints.forEach(p => { bounds.extend(p); any = true })
    if (any) map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 })

    return () => { if (polyline) polyline.setMap(null) }
  }, [map, locations, extraPoints])

  return null
}

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.md};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
`

const UserInfoCard = styled(Card)`
  margin-bottom: ${props => props.theme.spacing.lg};
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.md};
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`

const MapContainer = styled.div`
  height: 500px;
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.large};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing.lg};
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`

const StatCard = styled(Card)`
  text-align: center;
  padding: ${props => props.theme.spacing.md};
`

interface Activity {
  id: string
  type: string
  date: string
  yardName: string
  location?: { lat: number; lng: number }
  details?: string
  detailsKey?: string
  detailsParams?: Record<string, unknown>
}

// Stored (English) treatment types -> translation keys; custom types pass through
const TREATMENT_TYPE_KEYS: Record<string, string> = {
  'Treat for Varroa Mites': 'yardDetails.taskTypes.treatVarroaMites',
  'Feed Sugar Syrup': 'yardDetails.taskTypes.feedSugarSyrup',
  'Feed Pollen Substitute': 'yardDetails.taskTypes.feedPollenSubstitute',
  'Requeen Hive': 'yardDetails.taskTypes.reqeenHive',
  'Replace Old Frames': 'yardDetails.taskTypes.replaceOldFrames',
  'Clean Hive Equipment': 'yardDetails.taskTypes.cleanHiveEquipment',
}

// Activity type -> translation key (translated at render time)
const ACTIVITY_TYPE_KEYS: Record<string, string> = {
  inspection: 'inspection.title',
  treatment: 'treatment.title',
  harvest: 'harvest.title',
  movement: 'movement.title',
}

interface YardStats {
  yardId: string
  yardName: string
  visitCount: number
  totalTimeMinutes: number
  avgTimeMinutes: number
  location: { lat: number; lng: number }
}

/**
 * User details screen with activity tracking and map visualization
 */
function UserDetailsScreen(): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [user, setUser] = useState<User | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [yards, setYards] = useState<any[]>([])
  const [warehouses, setWarehouses] = useState<any[]>([])
  const [userLocations, setUserLocations] = useState<UserLocation[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState(0)
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('week')
  const [effectivePermissions, setEffectivePermissions] = useState<string[]>([])
  const [roleDefaults, setRoleDefaults] = useState<string[]>([])
  const [allPermissions, setAllPermissions] = useState<PermissionInfo[]>([])
  const [useCustomPerms, setUseCustomPerms] = useState(false)
  const [customPerms, setCustomPerms] = useState<string[]>([])
  const [permSaving, setPermSaving] = useState(false)

  useEffect(() => {
    async function loadUserData(): Promise<void> {
      try {
        setLoading(true)
        if (!userId) return

        const [
          usersRes,
          yardsRes,
          inspectionsRes,
          treatmentsRes,
          harvestsRes,
          movementsRes,
          inventoryRes,
        ] = await Promise.all([
          getUsers(),
          getYards(),
          getInspections(),
          getTreatments(),
          getHarvests(),
          getMovements(),
          getInventory(),
        ])

        const allUsers = usersRes.data || []
        const currentUser = allUsers.find(u => String(u.id) === String(userId))

        setUser(currentUser || null)
        setUsers(allUsers)
        setYards(yardsRes.data || [])
        // Warehouses are inventory entries that carry a warehouseId + coordinates
        const whObj: Record<string, any> = {}
        ;(inventoryRes.data || []).forEach((it: any) => {
          const key = String(it.warehouseId)
          if (it.warehouseId && it.latitude && it.longitude && !whObj[key]) {
            whObj[key] = { id: it.warehouseId, lat: Number(it.latitude), lng: Number(it.longitude), name: it.warehouseName || '' }
          }
        })
        setWarehouses(Object.values(whObj))

        // Fetch user GPS locations
        try {
          const locationsRes = await getUserLocations(userId)
          console.log('Fetched user locations:', locationsRes.data?.length || 0, 'points')
          setUserLocations(locationsRes.data || [])
        } catch (error) {
          console.error('Error fetching user locations:', error)
          setUserLocations([])
        }

        // Compile user activities
        const userActivities: Activity[] = []

        // Add inspections
        ;(inspectionsRes.data || []).forEach((inspection: any) => {
          if (String(inspection.userId) === String(userId)) {
            const yard = yardsRes.data?.find((y: any) => String(y.id || y._id) === String(inspection.yardId))
            userActivities.push({
              id: `inspection-${inspection.id}`,
              type: 'inspection',
              date: inspection.date,
              yardName: yard?.name || '',
              location: yard ? { lat: Number(yard.latitude), lng: Number(yard.longitude) } : undefined,
              detailsKey: 'userDetails.activity.statusRecorded',
            })
          }
        })

        // Add treatments
        ;(treatmentsRes.data || []).forEach((treatment: any) => {
          if (String(treatment.userId) === String(userId)) {
            const yard = yardsRes.data?.find((y: any) => String(y.id || y._id) === String(treatment.yardId))
            userActivities.push({
              id: `treatment-${treatment.id}`,
              type: 'treatment',
              date: treatment.date,
              yardName: yard?.name || '',
              location: yard ? { lat: Number(yard.latitude), lng: Number(yard.longitude) } : undefined,
              details: treatment.treatmentType,
              detailsKey: TREATMENT_TYPE_KEYS[treatment.treatmentType],
            })
          }
        })

        // Add harvests
        ;(harvestsRes.data || []).forEach((harvest: any) => {
          if (String(harvest.userId) === String(userId)) {
            const yard = yardsRes.data?.find((y: any) => String(y.id || y._id) === String(harvest.yardId))
            userActivities.push({
              id: `harvest-${harvest.id}`,
              type: 'harvest',
              date: harvest.date,
              yardName: yard?.name || '',
              location: yard ? { lat: Number(yard.latitude), lng: Number(yard.longitude) } : undefined,
              detailsKey: 'userDetails.activity.harvestDetails',
              detailsParams: { quantity: harvest.quantity, format: harvest.format, honeyType: harvest.honeyType, enums: true },
            })
          }
        })

        // Add movements
        ;(movementsRes.data || []).forEach((movement: any) => {
          if (String(movement.userId) === String(userId)) {
            const yard = yardsRes.data?.find((y: any) => String(y.id || y._id) === String(movement.fromYardId))
            userActivities.push({
              id: `movement-${movement.id}`,
              type: 'movement',
              date: movement.date || movement.createdAt,
              yardName: yard?.name || '',
              location: yard ? { lat: Number(yard.latitude), lng: Number(yard.longitude) } : undefined,
              detailsKey: 'userDetails.activity.movementDetails',
              detailsParams: { quantity: movement.quantity, boxType: movement.boxType, enums: true },
            })
          }
        })

        // Sort by date descending
        userActivities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        setActivities(userActivities)

        // Load permissions
        try {
          const permRes = await getUserPermissions(userId)
          const permData = permRes.data
          setEffectivePermissions(permData.effectivePermissions)
          setRoleDefaults(permData.roleDefaults)
          setAllPermissions(permData.allPermissions)
          setUseCustomPerms(permData.customPermissions)
          setCustomPerms(permData.permissions)
        } catch (permErr) {
          console.error('Error loading permissions:', permErr)
        }
      } catch (error) {
        console.error('Error loading user data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadUserData()
  }, [userId])

  const filteredActivities = useMemo(() => {
    const now = new Date()
    const filterDate = new Date()

    if (dateFilter === 'today') {
      filterDate.setHours(0, 0, 0, 0)
    } else if (dateFilter === 'week') {
      filterDate.setDate(now.getDate() - 7)
    } else if (dateFilter === 'month') {
      filterDate.setDate(now.getDate() - 30)
    } else {
      return activities
    }

    return activities.filter(a => new Date(a.date) >= filterDate)
  }, [activities, dateFilter])

  const yardStats = useMemo<YardStats[]>(() => {
    const stats: Record<string, YardStats> = {}

    filteredActivities.forEach(activity => {
      const yard = yards.find(y => (y.name === activity.yardName))
      if (!yard) return

      const yardId = String(yard.id || yard._id)
      if (!stats[yardId]) {
        stats[yardId] = {
          yardId,
          yardName: yard.name,
          visitCount: 0,
          totalTimeMinutes: 0,
          avgTimeMinutes: 0,
          location: { lat: Number(yard.latitude), lng: Number(yard.longitude) },
        }
      }

      stats[yardId].visitCount++
      // Estimate 30 minutes per activity
      stats[yardId].totalTimeMinutes += 30
      stats[yardId].avgTimeMinutes = stats[yardId].totalTimeMinutes / stats[yardId].visitCount
    })

    return Object.values(stats).sort((a, b) => b.visitCount - a.visitCount)
  }, [filteredActivities, yards])

  const mapCenter = useMemo(() => {
    if (yardStats.length > 0) {
      const avgLat = yardStats.reduce((sum, s) => sum + s.location.lat, 0) / yardStats.length
      const avgLng = yardStats.reduce((sum, s) => sum + s.location.lng, 0) / yardStats.length
      return { lat: avgLat, lng: avgLng }
    }
    return { lat: 32.0853, lng: 34.7818 } // Default center
  }, [yardStats])

  const userTrailPath = useMemo(() => {
    return filteredActivities
      .filter(a => a.location)
      .map(a => ({ lat: a.location!.lat, lng: a.location!.lng }))
  }, [filteredActivities])

  const parentUser = useMemo(() => {
    if (!user?.parentAccountId) return null
    return users.find(u => String(u.id) === String(user.parentAccountId))
  }, [user, users])

  // How long the user stayed at a point — time span of GPS fixes within ~160m.
  const dwellMinutes = (lat: number, lng: number): number => {
    const R = 0.0016
    const times = userLocations
      .filter(l => Math.abs(l.latitude - lat) < R && Math.abs(l.longitude - lng) < R)
      .map(l => new Date(l.timestamp).getTime())
    if (times.length < 2) return 0
    return Math.round((Math.max(...times) - Math.min(...times)) / 60000)
  }
  const fmtDwell = (m: number): string => (m >= 60
    ? t('userDetails.duration.hoursMinutes', { hours: Math.floor(m / 60), minutes: m % 60 })
    : t('userDetails.duration.minutes', { minutes: m }))

  // All sites (yards) with coordinates — shown on the map; visited ones carry a visit count
  const siteMarkers = useMemo(() => {
    return yards
      .filter(y => y.latitude && y.longitude)
      .map(y => {
        const id = String(y.id || y._id)
        const stat = yardStats.find(s => s.yardId === id || s.yardName === y.name)
        return { id, name: y.name, lat: Number(y.latitude), lng: Number(y.longitude), visitCount: stat?.visitCount || 0 }
      })
  }, [yards, yardStats])

  // Bounds points so the map shows every site + warehouse + the route
  const mapExtraPoints = useMemo(
    () => [...siteMarkers.map(s => ({ lat: s.lat, lng: s.lng })), ...warehouses.map(w => ({ lat: w.lat, lng: w.lng }))],
    [siteMarkers, warehouses]
  )

  // Harvest/movement params carry raw API enums (honey type, format, box type) — map to labels
  function localizeDetailParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!params || !params.enums) return params
    const out: Record<string, unknown> = { ...params }
    if (params.honeyType) out.honeyType = t(`inventory.honeyTypes.${params.honeyType}`, { defaultValue: String(params.honeyType) })
    if (params.boxType) out.boxType = t(`inventory.boxTypes.${params.boxType}`, { defaultValue: String(params.boxType) })
    if (params.format) {
      out.format = params.format === 'barrel' ? t('inventory.barrel')
        : params.format === 'comb' || params.format === 'combHoney' ? t('inventory.combHoney')
        : String(params.format)
    }
    return out
  }

  if (loading) {
    return <Container><Typography>{t('common.loading')}</Typography></Container>
  }

  if (!user) {
    return <Container><Typography>{t('userDetails.notFound')}</Typography></Container>
  }

  return (
    <Container>
      <Header>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/user-management')}>
          {t('button.back.text')}
        </Button>
        <Typography variant="h4" fontWeight={700}>
          {user.name}
        </Typography>
        <Chip 
          label={t(`users.roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') })}
          color={user.isActive ? 'success' : 'default'}
          size="small"
        />
      </Header>

      <UserInfoCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('userDetails.userInformation')}</Typography>
          <Divider sx={{ my: 2 }} />
          <InfoGrid>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.email')}</Typography>
              <Typography variant="body1">{user.email}</Typography>
            </InfoItem>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.role')}</Typography>
              <Typography variant="body1">{t(`users.roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') })}</Typography>
            </InfoItem>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.status')}</Typography>
              <Typography variant="body1">{user.isActive ? t('users.active') : t('users.inactive')}</Typography>
            </InfoItem>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.parentAccount')}</Typography>
              <Typography variant="body1">{parentUser?.name || t('userDetails.none')}</Typography>
            </InfoItem>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.lastLogin')}</Typography>
              <Typography variant="body1">
                {user.lastLogin ? new Date(user.lastLogin).toLocaleString(i18n.language) : t('users.never')}
              </Typography>
            </InfoItem>
            <InfoItem>
              <Typography variant="caption" color="text.secondary">{t('userDetails.labels.created')}</Typography>
              <Typography variant="body1">{new Date(user.createdAt).toLocaleDateString(i18n.language)}</Typography>
            </InfoItem>
          </InfoGrid>
        </CardContent>
      </UserInfoCard>

      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button 
          size="small" 
          variant={dateFilter === 'today' ? 'contained' : 'outlined'}
          onClick={() => setDateFilter('today')}
        >
          {t('userDetails.dateFilter.today')}
        </Button>
        <Button 
          size="small" 
          variant={dateFilter === 'week' ? 'contained' : 'outlined'}
          onClick={() => setDateFilter('week')}
        >
          {t('userDetails.dateFilter.week')}
        </Button>
        <Button 
          size="small" 
          variant={dateFilter === 'month' ? 'contained' : 'outlined'}
          onClick={() => setDateFilter('month')}
        >
          {t('userDetails.dateFilter.month')}
        </Button>
        <Button 
          size="small" 
          variant={dateFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setDateFilter('all')}
        >
          {t('userDetails.dateFilter.all')}
        </Button>
      </Box>

      <StatsGrid>
        <StatCard>
          <Typography variant="h4" color="primary">{filteredActivities.length}</Typography>
          <Typography variant="caption" color="text.secondary">{t('userDetails.stats.totalActivities')}</Typography>
        </StatCard>
        <StatCard>
          <Typography variant="h4" color="primary">{yardStats.length}</Typography>
          <Typography variant="caption" color="text.secondary">{t('userDetails.stats.yardsVisited')}</Typography>
        </StatCard>
        <StatCard>
          <Typography variant="h4" color="primary">
            {t('userDetails.duration.hours', { hours: Math.round(yardStats.reduce((sum, s) => sum + s.totalTimeMinutes, 0) / 60) })}
          </Typography>
          <Typography variant="caption" color="text.secondary">{t('userDetails.stats.totalTime')}</Typography>
        </StatCard>
        <StatCard>
          <Typography variant="h4" color="primary">
            {t('userDetails.duration.minutes', { minutes: yardStats.length > 0 ? Math.round(yardStats.reduce((sum, s) => sum + s.avgTimeMinutes, 0) / yardStats.length) : 0 })}
          </Typography>
          <Typography variant="caption" color="text.secondary">{t('userDetails.stats.avgTimePerYard')}</Typography>
        </StatCard>
      </StatsGrid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label={t('userDetails.tabs.activityMap')} />
            <Tab label={t('userDetails.tabs.activityLog')} />
            <Tab label={t('userDetails.tabs.yardStatistics')} />
            <Tab label={t('userDetails.tabs.permissions')} />
          </Tabs>

          {tab === 0 && (() => {
            console.log('Rendering map with', userLocations.length, 'location points')
            console.log('Sample location:', userLocations[0])
            return (
              <Box sx={{ mt: 2 }}>
                <MapContainer>
                  <APIProvider apiKey={getGoogleMapsApiKey()}>
                    <Map
                      defaultCenter={mapCenter}
                      defaultZoom={12}
                      mapId="user-activity-map"
                    >
                      {/* GPS Route Trail as polyline */}
                      <RoutePolyline locations={userLocations} extraPoints={mapExtraPoints} />
                    
                      {/* Site (yard) markers — ALL sites, same hive design as the dashboard */}
                      {siteMarkers.map((site) => {
                        const dwell = dwellMinutes(site.lat, site.lng)
                        const visited = site.visitCount > 0 || dwell > 0
                        const gid = `act-yard-${site.id}`
                        return (
                          <AdvancedMarker key={site.id} position={{ lat: site.lat, lng: site.lng }} title={visited ? t('userDetails.map.siteTooltip', { name: site.name, visits: t('userDetails.map.visitCount', { count: site.visitCount }), duration: fmtDwell(dwell) }) : site.name}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', filter: 'drop-shadow(0 6px 14px rgba(217,154,0,0.38))', opacity: visited ? 1 : 0.72 }}>
                              <svg width="54" height="64" viewBox="0 0 54 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                  <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#f7c948" />
                                    <stop offset="100%" stopColor="#d99a00" />
                                  </linearGradient>
                                </defs>
                                <path d="M27 3C15.402 3 6 12.402 6 24c0 14.2 21 34 21 34s21-19.8 21-34C48 12.402 38.598 3 27 3z" fill={`url(#${gid})`} />
                                <circle cx="27" cy="24" r="13" fill="rgba(255,248,225,0.18)" />
                                <circle cx="27" cy="24" r="10.5" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.24)" strokeWidth="1.2" />
                                <path d="M20.5 17.5H33.5L31.5 20.5H22.5L20.5 17.5Z" fill="#fffdf7" fillOpacity="0.95" />
                                <rect x="21.5" y="21" width="11" height="3.2" rx="1.6" fill="#fffdf7" fillOpacity="0.95" />
                                <rect x="20.5" y="25" width="13" height="3.3" rx="1.65" fill="#fffdf7" fillOpacity="0.9" />
                                <rect x="19.5" y="29.3" width="15" height="3.5" rx="1.75" fill="#fffdf7" fillOpacity="0.85" />
                                <circle cx="27" cy="31" r="1.6" fill="#8D6E63" />
                              </svg>
                              <div style={{ marginTop: -2, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(15,23,42,0.90)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', maxWidth: 160, border: '1px solid #f7c94866', boxShadow: '0 6px 14px rgba(15,23,42,0.22)' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{site.name}</span>
                                {visited && <span style={{ background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, padding: '1px 6px', fontSize: 9, fontWeight: 800, flexShrink: 0 }}>⏱ {fmtDwell(dwell)}</span>}
                              </div>
                            </div>
                          </AdvancedMarker>
                        )
                      })}

                      {/* Warehouse markers — same warehouse design as the dashboard */}
                      {warehouses.map((wh, index) => {
                        const dwell = dwellMinutes(wh.lat, wh.lng)
                        return (
                          <AdvancedMarker key={`wh-${wh.id}`} position={{ lat: wh.lat, lng: wh.lng }} title={t('userDetails.map.warehouseTooltip', { name: wh.name || t('userDetails.map.warehouseFallback', { id: wh.id }), duration: fmtDwell(dwell) })}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', filter: 'drop-shadow(0 6px 14px rgba(55,71,79,0.42))' }}>
                              <svg width="48" height="58" viewBox="0 0 48 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                  <linearGradient id={`act-wh-${index}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#607D8B" />
                                    <stop offset="100%" stopColor="#37474F" />
                                  </linearGradient>
                                </defs>
                                <path d="M24 2C13.51 2 5 10.51 5 21c0 13.5 19 35 19 35s19-21.5 19-35C43 10.51 34.49 2 24 2z" fill={`url(#act-wh-${index})`} />
                                <circle cx="24" cy="21" r="13" fill="rgba(255,255,255,0.14)" />
                                <circle cx="24" cy="21" r="10.5" fill="rgba(15,23,42,0.18)" stroke="rgba(255,255,255,0.28)" strokeWidth="1.2" />
                                <path d="M17 23L24 17L31 23" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="18" y="23" width="12" height="8.5" rx="1.5" fill="white" fillOpacity="0.92" />
                                <rect x="22" y="26" width="4" height="5.5" rx="0.8" fill="#455A64" />
                              </svg>
                              <div style={{ marginTop: -2, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(15,23,42,0.90)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap', maxWidth: 150, border: '1px solid rgba(96,125,139,0.45)', boxShadow: '0 6px 14px rgba(15,23,42,0.25)' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{wh.name || t('userDetails.map.warehouseFallback', { id: wh.id })}</span>
                                <span style={{ background: 'rgba(255,255,255,0.16)', borderRadius: 999, padding: '1px 6px', fontSize: 9, fontWeight: 800, flexShrink: 0 }}>⏱ {fmtDwell(dwell)}</span>
                              </div>
                            </div>
                          </AdvancedMarker>
                        )
                      })}
                    </Map>
                  </APIProvider>
                </MapContainer>
                <Typography variant="caption" color="text.secondary">
                  {t('userDetails.map.legend')}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  {userLocations.length > 0 && (
                    t('userDetails.map.routeRange', {
                      from: new Date(userLocations[0]?.timestamp).toLocaleString(i18n.language),
                      to: new Date(userLocations[userLocations.length - 1]?.timestamp).toLocaleString(i18n.language),
                      points: t('userDetails.map.gpsPoints', { count: userLocations.length }),
                    })
                  )}
                </Typography>
              </Box>
            )
          })()}

          {tab === 1 && (
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('form.label.date')}</TableCell>
                    <TableCell>{t('userDetails.table.type')}</TableCell>
                    <TableCell>{t('userDetails.table.yard')}</TableCell>
                    <TableCell>{t('userDetails.table.details')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivities.map(activity => (
                    <TableRow key={activity.id}>
                      <TableCell>{new Date(activity.date).toLocaleString(i18n.language)}</TableCell>
                      <TableCell>
                        <Chip label={ACTIVITY_TYPE_KEYS[activity.type] ? t(ACTIVITY_TYPE_KEYS[activity.type]) : activity.type} size="small" />
                      </TableCell>
                      <TableCell>{activity.yardName || t('userDetails.unknownYard')}</TableCell>
                      <TableCell>{activity.detailsKey ? t(activity.detailsKey, localizeDetailParams(activity.detailsParams)) : activity.details}</TableCell>
                    </TableRow>
                  ))}
                  {filteredActivities.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('userDetails.noActivities')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('userDetails.table.yard')}</TableCell>
                    <TableCell align="right">{t('userDetails.table.visits')}</TableCell>
                    <TableCell align="right">{t('userDetails.stats.totalTime')}</TableCell>
                    <TableCell align="right">{t('userDetails.table.avgTimePerVisit')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {yardStats.map(stat => (
                    <TableRow key={stat.yardId}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOnIcon fontSize="small" color="action" />
                          {stat.yardName}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{stat.visitCount}</TableCell>
                      <TableCell align="right">{t('userDetails.duration.minutesLong', { minutes: Math.round(stat.totalTimeMinutes) })}</TableCell>
                      <TableCell align="right">{t('userDetails.duration.minutesLong', { minutes: Math.round(stat.avgTimeMinutes) })}</TableCell>
                    </TableRow>
                  ))}
                  {yardStats.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('userDetails.noYardVisits')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('userDetails.perms.effective')}
                  <Chip
                    label={useCustomPerms ? t('userDetails.perms.custom') : t('userDetails.perms.roleDefaults')}
                    size="small"
                    color={useCustomPerms ? 'warning' : 'info'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant={!useCustomPerms ? 'contained' : 'outlined'}
                    onClick={async () => {
                      setUseCustomPerms(false)
                      setCustomPerms([])
                      setEffectivePermissions(roleDefaults)
                      if (userId) {
                        setPermSaving(true)
                        try {
                          await updateUserPermissions(userId, { customPermissions: false })
                        } catch (err) {
                          console.error('Error resetting permissions:', err)
                        } finally {
                          setPermSaving(false)
                        }
                      }
                    }}
                    disabled={permSaving}
                  >
                    {t('userDetails.perms.useRoleDefaults')}
                  </Button>
                  <Button
                    size="small"
                    variant={useCustomPerms ? 'contained' : 'outlined'}
                    onClick={() => {
                      setUseCustomPerms(true)
                      if (customPerms.length === 0) {
                        setCustomPerms([...roleDefaults])
                        setEffectivePermissions([...roleDefaults])
                      }
                    }}
                    disabled={permSaving}
                  >
                    {t('userDetails.perms.customPermissions')}
                  </Button>
                </Box>
              </Box>

              {(() => {
                const grouped: Record<string, PermissionInfo[]> = {}
                for (const perm of allPermissions) {
                  if (!grouped[perm.category]) grouped[perm.category] = []
                  grouped[perm.category].push(perm)
                }
                return Object.entries(grouped).map(([category, perms]) => (
                  <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ borderBottom: '1px solid #e0e0e0', pb: 0.5, mb: 1 }}>
                      {category}
                    </Typography>
                    {perms.map(perm => {
                      const isActive = effectivePermissions.includes(perm.key)
                      const isRoleDefault = roleDefaults.includes(perm.key)
                      return (
                        <Box key={perm.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <input
                            type="checkbox"
                            checked={isActive}
                            disabled={!useCustomPerms || permSaving}
                            onChange={() => {
                              if (!useCustomPerms) return
                              const newPerms = isActive
                                ? customPerms.filter(p => p !== perm.key)
                                : [...customPerms, perm.key]
                              setCustomPerms(newPerms)
                              setEffectivePermissions(newPerms)
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {perm.label}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {perm.description}
                            </Typography>
                          </Typography>
                          {isActive && (
                            <Chip
                              label={useCustomPerms ? t('userDetails.perms.sourceCustom') : isRoleDefault ? t('userDetails.perms.sourceRole') : t('userDetails.perms.sourceCustom')}
                              size="small"
                              variant="outlined"
                              color={useCustomPerms ? 'warning' : 'info'}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                ))
              })()}

              {useCustomPerms && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!userId) return
                      setPermSaving(true)
                      try {
                        await updateUserPermissions(userId, {
                          customPermissions: true,
                          permissions: customPerms,
                        })
                      } catch (err: any) {
                        alert(err.response?.data?.error || t('userDetails.perms.saveError'))
                      } finally {
                        setPermSaving(false)
                      }
                    }}
                    disabled={permSaving}
                  >
                    {permSaving ? t('button.save.loading') : t('userDetails.perms.save')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  )
}

export default UserDetailsScreen
